import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
    Input,
    Select,
    VStack,
    Text,
    Box,
    Radio,
    RadioGroup,
    Stack,
    Flex,
    useToast,
} from '@chakra-ui/react';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
    emptyColumnMapping,
    IGNORE_COLUMN_ID,
    mappingFromFuzzyMatchResponse,
    UNASSIGNED_COLUMN_ID,
    type ImportColumnMapping,
    type ImportTargetField,
} from '../../functions/import/fuzzyColumnMatch';
import { INVENTORY_IMPORT_UNION_FIELD_IDS } from '../../functions/import/inventoryImportFields';
import {
    applyColumnMapping,
    parseTabularFile,
} from '../../functions/import/parseTabularFile';
import { useFuzzyMatchImportColumns } from '../../hooks/useFuzzyMatchImportColumns';
import { Icon, IconButton } from '../../ui';
import { ImportColumnMapper } from './ImportColumnMapper';

const MotionModalContent = motion(ModalContent);

type ModalViewMode = 'order' | 'manual';

/** Manual quiz: source column → target field id or IGNORE_COLUMN_ID */
type ManualAnswers = Record<string, string>;

export interface ImportMappingModalProps {
    isOpen: boolean;
    onClose: () => void;
    file: File | null;
    targetFields: ImportTargetField[];
    /** Optional constant field renderers keyed by field id (e.g. category select). */
    renderConstantField?: (
        field: ImportTargetField,
        value: string,
        onChange: (value: string) => void,
        error?: string,
    ) => React.ReactNode;
    /**
     * Optional validator for Default Values. Return fieldId → error message.
     * Called on Start Import; blocks submit when non-empty.
     */
    validateConstants?: (
        constants: Record<string, string>,
        fieldsNeedingConstant: ImportTargetField[],
    ) => Record<string, string>;
    onSubmit: (mappedRows: Array<Record<string, string>>) => Promise<string | void> | string | void;
    /** After successful submit, navigate here unless onSubmit returns a path. */
    redirectUri?: string;
    submitLabel?: string;
}

function buildMappingFromManualAnswers(
    sourceColumns: string[],
    targetFields: ImportTargetField[],
    answers: ManualAnswers,
): ImportColumnMapping {
    const mapping: ImportColumnMapping = {
        [UNASSIGNED_COLUMN_ID]: [],
        [IGNORE_COLUMN_ID]: [],
    };
    targetFields.forEach((f) => {
        mapping[f.id] = [];
    });
    sourceColumns.forEach((source) => {
        const target = answers[source];
        if (!target) {
            mapping[UNASSIGNED_COLUMN_ID].push(source);
        } else if (target === IGNORE_COLUMN_ID) {
            mapping[IGNORE_COLUMN_ID].push(source);
        } else if (mapping[target]) {
            mapping[target].push(source);
        } else {
            mapping[UNASSIGNED_COLUMN_ID].push(source);
        }
    });
    return mapping;
}

function buildManualAnswersFromMapping(
    sourceColumns: string[],
    mapping: ImportColumnMapping,
): ManualAnswers {
    const answers: ManualAnswers = {};
    const reverse = new Map<string, string>();
    Object.entries(mapping).forEach(([colId, sources]) => {
        sources.forEach((s) => reverse.set(s, colId));
    });
    sourceColumns.forEach((source) => {
        const col = reverse.get(source);
        if (col && col !== UNASSIGNED_COLUMN_ID) {
            answers[source] = col;
        }
    });
    return answers;
}

function computeConstantErrors(
    validateConstants:
        | ((
              constants: Record<string, string>,
              fieldsNeedingConstant: ImportTargetField[],
          ) => Record<string, string>)
        | undefined,
    constants: Record<string, string>,
    fieldsNeedingConstant: ImportTargetField[],
): Record<string, string> {
    if (validateConstants) {
        return validateConstants(constants, fieldsNeedingConstant);
    }
    return Object.fromEntries(
        fieldsNeedingConstant
            .filter((f) => !(constants[f.id] ?? '').trim())
            .filter(
                (f) =>
                    f.id !== 'reorderQuantity' &&
                    f.id !== 'targetQuantity' &&
                    f.id !== 'initialInStockQuantity',
            )
            .map((f) => [f.id, `${f.label} is required.`]),
    );
}

export function ImportMappingModal({
    isOpen,
    onClose,
    file,
    targetFields,
    renderConstantField,
    validateConstants,
    onSubmit,
    redirectUri,
    submitLabel = 'Start Import',
}: ImportMappingModalProps) {
    const toast = useToast();
    const navigate = useNavigate();
    const fuzzyMatchMutation = useFuzzyMatchImportColumns();
    const [headers, setHeaders] = useState<string[]>([]);
    const [rows, setRows] = useState<Array<Record<string, string>>>([]);
    const [mapping, setMapping] = useState<ImportColumnMapping>({});
    const [constants, setConstants] = useState<Record<string, string>>({});
    const [constantErrors, setConstantErrors] = useState<Record<string, string>>({});
    const [parsing, setParsing] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [viewMode, setViewMode] = useState<ModalViewMode>('order');
    const [manualAnswers, setManualAnswers] = useState<ManualAnswers>({});
    const [expandedSource, setExpandedSource] = useState<string | null>(null);
    const expandedSectionRef = useRef<HTMLDivElement | null>(null);
    const modalContentRef = useRef<HTMLDivElement | null>(null);
    const [modalWidth, setModalWidth] = useState<number | null>(null);

    useEffect(() => {
        if (!isOpen || !file) return;
        let cancelled = false;
        setParsing(true);
        setViewMode('order');
        setManualAnswers({});
        setExpandedSource(null);
        setModalWidth(null);
        parseTabularFile(file)
            .then(async (parsed) => {
                if (cancelled) return;
                setHeaders(parsed.headers);
                setRows(parsed.rows);
                setConstants({});
                setConstantErrors({});
                try {
                    const response = await fuzzyMatchMutation.mutateAsync({
                        sourceColumns: parsed.headers,
                        targetFields: targetFields.map((f) => ({ id: f.id, label: f.label })),
                    });
                    if (cancelled) return;
                    setMapping(mappingFromFuzzyMatchResponse(response, targetFields));
                } catch {
                    if (cancelled) return;
                    setMapping(emptyColumnMapping(parsed.headers, targetFields));
                    toast({
                        title: 'Could not auto-match columns',
                        description: 'Map columns manually or use Manually Assign.',
                        status: 'warning',
                        duration: 5000,
                        isClosable: true,
                    });
                }
            })
            .catch((err: unknown) => {
                toast({
                    title: 'Failed to parse file',
                    description: err instanceof Error ? err.message : 'Unknown error',
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                onClose();
            })
            .finally(() => {
                if (!cancelled) setParsing(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, file]);

    const fieldsNeedingConstant = useMemo(
        () =>
            targetFields.filter(
                (f) => f.required && (mapping[f.id]?.length ?? 0) === 0,
            ),
        [targetFields, mapping],
    );

    // Seed quantity defaults so 0 is a valid submitted value
    useEffect(() => {
        setConstants((prev) => {
            let changed = false;
            const next = { ...prev };
            fieldsNeedingConstant.forEach((f) => {
                if (
                    (f.id === 'reorderQuantity' ||
                        f.id === 'targetQuantity' ||
                        f.id === 'initialInStockQuantity') &&
                    next[f.id] === undefined
                ) {
                    next[f.id] = '0';
                    changed = true;
                }
                // Seed select defaults so the visible first option is also stored in
                // constants (controlled selects otherwise look filled while value stays '').
                if (f.id === 'partType' && !next[f.id]) {
                    next[f.id] = 'oem';
                    changed = true;
                }
                if (f.id === 'category' && !next[f.id]) {
                    next[f.id] = 'part';
                    changed = true;
                }
                if (f.id === 'unit' && !next[f.id]) {
                    next[f.id] = 'each';
                    changed = true;
                }
            });
            return changed ? next : prev;
        });
    }, [fieldsNeedingConstant]);

    const defaultsValid = useMemo(() => {
        if (fieldsNeedingConstant.length === 0) return true;
        return (
            Object.keys(
                computeConstantErrors(validateConstants, constants, fieldsNeedingConstant),
            ).length === 0
        );
    }, [constants, fieldsNeedingConstant, validateConstants]);

    const unassignedCount = mapping[UNASSIGNED_COLUMN_ID]?.length ?? 0;
    const canSubmit =
        viewMode === 'order' &&
        !parsing &&
        unassignedCount === 0 &&
        rows.length > 0 &&
        !submitting &&
        defaultsValid;

    useEffect(() => {
        if (viewMode !== 'manual' || !expandedSource) return;
        const el = expandedSectionRef.current;
        if (!el) return;
        requestAnimationFrame(() => {
            el.scrollIntoView({ block: 'end', behavior: 'smooth' });
        });
    }, [viewMode, expandedSource]);

    // Measure natural width after content changes; animate width (not layout/scale).
    useLayoutEffect(() => {
        const el = modalContentRef.current;
        if (!el || !isOpen) return;

        const previousWidth = el.style.width;
        el.style.width = 'fit-content';
        const measured = el.getBoundingClientRect().width;
        el.style.width = previousWidth;

        const nextWidth = Math.min(measured, window.innerWidth * 0.95);
        setModalWidth((prev) => (prev != null && Math.abs(prev - nextWidth) < 0.5 ? prev : nextWidth));
    }, [
        isOpen,
        viewMode,
        parsing,
        headers,
        mapping,
        manualAnswers,
        expandedSource,
        fieldsNeedingConstant.length,
    ]);

    const handleOpenManual = () => {
        const answers = buildManualAnswersFromMapping(headers, mapping);
        setManualAnswers(answers);
        const firstUnanswered = headers.find((h) => !answers[h]);
        setExpandedSource(firstUnanswered ?? null);
        setViewMode('manual');
    };

    const advanceAfterAssign = (answers: ManualAnswers, currentSource: string) => {
        const nextUnanswered =
            headers.find((h) => h !== currentSource && !answers[h]) ??
            headers.find((h) => !answers[h]) ??
            null;
        setExpandedSource(nextUnanswered);
    };

    const handleManualSelect = (source: string, targetId: string) => {
        setManualAnswers((prev) => {
            const next = { ...prev, [source]: targetId };
            advanceAfterAssign(next, source);
            return next;
        });
    };

    const handleManualRadioClick = (source: string, targetId: string, isSelected: boolean) => {
        if (isSelected) {
            setManualAnswers((prev) => {
                advanceAfterAssign(prev, source);
                return prev;
            });
            return;
        }
        handleManualSelect(source, targetId);
    };

    const handleClearManual = () => {
        setManualAnswers({});
        setExpandedSource(headers[0] ?? null);
    };

    const handleOrderAssignments = () => {
        setMapping(buildMappingFromManualAnswers(headers, targetFields, manualAnswers));
        setViewMode('order');
    };

    const handleSubmit = async () => {
        if (!canSubmit) return;

        const errors = computeConstantErrors(
            validateConstants,
            constants,
            fieldsNeedingConstant,
        );
        setConstantErrors(errors);
        if (Object.keys(errors).length > 0) {
            toast({
                title: 'Fix Default Values',
                description: 'Provide valid values for each required field with no mapped columns.',
                status: 'warning',
                duration: 4000,
                isClosable: true,
            });
            return;
        }

        setSubmitting(true);
        try {
            const mapped = applyColumnMapping(
                rows,
                mapping,
                constants,
                targetFields.map((f) => f.id),
                INVENTORY_IMPORT_UNION_FIELD_IDS,
            );
            const result = await onSubmit(mapped);
            const nextUri = typeof result === 'string' ? result : redirectUri;
            if (nextUri) {
                navigate(nextUri);
            }
            onClose();
        } catch (err: unknown) {
            toast({
                title: 'Import failed',
                description: err instanceof Error ? err.message : 'Unknown error',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setSubmitting(false);
        }
    };

    const radioOptions = useMemo(
        () => targetFields.map((f) => ({ value: f.id, label: f.label })),
        [targetFields],
    );

    const answerDisplayLabel = (answer: string) => {
        if (answer === IGNORE_COLUMN_ID) return 'Ignore';
        return radioOptions.find((o) => o.value === answer)?.label ?? answer;
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} scrollBehavior="inside" isCentered>
            <ModalOverlay />
            <MotionModalContent
                ref={modalContentRef}
                maxH="90vh"
                maxW="95vw"
                w={modalWidth == null ? 'fit-content' : undefined}
                initial={false}
                animate={modalWidth != null ? { width: modalWidth } : undefined}
                transition={{ duration: 0.28, ease: 'easeInOut' }}
                style={{ transformOrigin: 'center center' }}
            >
                <ModalHeader>
                    <Flex align="center" justify="space-between" gap={3}>
                        <Text>Map Import Columns</Text>
                        <Flex align="center" gap={2} flexShrink={0}>
                            {!parsing && viewMode === 'order' ? (
                                <Button size="sm" variant="outline" onClick={handleOpenManual}>
                                    Manually Assign
                                </Button>
                            ) : null}
                            {!parsing && viewMode === 'manual' ? (
                                <Button size="sm" variant="outline" onClick={handleClearManual}>
                                    Clear
                                </Button>
                            ) : null}
                            <IconButton
                                aria-label="Close"
                                icon={<Icon name="x" size="md" />}
                                size="sm"
                                variant="ghost"
                                colorScheme="gray"
                                color="fg.muted"
                                onClick={onClose}
                            />
                        </Flex>
                    </Flex>
                </ModalHeader>
                <ModalBody>
                    {parsing ? (
                        <Text color="gray.600">Parsing file…</Text>
                    ) : viewMode === 'manual' ? (
                        <VStack align="stretch" spacing={3}>
                            <Text fontSize="sm" color="gray.600">
                                Ignore or select a target field for each source column.
                            </Text>
                            {headers.map((source) => {
                                const answer = manualAnswers[source];
                                const collapsed = !!answer && expandedSource !== source;
                                const answerLabel = answer ? answerDisplayLabel(answer) : null;

                                if (collapsed) {
                                    return (
                                        <Box
                                            key={source}
                                            as="button"
                                            type="button"
                                            textAlign="left"
                                            px={3}
                                            py={2}
                                            borderWidth="1px"
                                            borderColor="gray.200"
                                            borderRadius="md"
                                            bg="gray.50"
                                            _hover={{ bg: 'gray.100' }}
                                            onClick={() => setExpandedSource(source)}
                                        >
                                            <Text fontSize="sm" color="gray.800">
                                                <Text as="span" fontWeight="semibold">
                                                    {source}
                                                </Text>
                                                {' → '}
                                                {answerLabel}
                                            </Text>
                                        </Box>
                                    );
                                }

                                return (
                                    <Box
                                        key={source}
                                        ref={
                                            expandedSource === source
                                                ? expandedSectionRef
                                                : undefined
                                        }
                                        borderWidth="1px"
                                        borderColor="blue.200"
                                        borderRadius="md"
                                        p={4}
                                        bg="white"
                                    >
                                        <Flex align="center" justify="space-between" gap={2} mb={3}>
                                            <Text fontSize="sm" fontWeight="semibold">
                                                {source}
                                            </Text>
                                            <Button
                                                size="xs"
                                                variant={
                                                    answer === IGNORE_COLUMN_ID
                                                        ? 'solid'
                                                        : 'outline'
                                                }
                                                onClick={() =>
                                                    handleManualSelect(source, IGNORE_COLUMN_ID)
                                                }
                                            >
                                                Ignore
                                            </Button>
                                        </Flex>
                                        <RadioGroup value={answer ?? ''}>
                                            <Stack spacing={2}>
                                                {radioOptions.map((opt) => {
                                                    const isSelected = answer === opt.value;
                                                    return (
                                                        <Box
                                                            key={opt.value}
                                                            cursor="pointer"
                                                            onClick={() =>
                                                                handleManualRadioClick(
                                                                    source,
                                                                    opt.value,
                                                                    isSelected,
                                                                )
                                                            }
                                                        >
                                                            <Radio
                                                                value={opt.value}
                                                                size="sm"
                                                                pointerEvents="none"
                                                            >
                                                                {opt.label}
                                                            </Radio>
                                                        </Box>
                                                    );
                                                })}
                                            </Stack>
                                        </RadioGroup>
                                    </Box>
                                );
                            })}
                        </VStack>
                    ) : (
                        <VStack align="stretch" spacing={6}>
                            <Text fontSize="sm" color="gray.600">
                                {rows.length} row{rows.length === 1 ? '' : 's'} · drag source
                                columns onto target fields. Tags, Bins, and part-number fields
                                union all mapped sources; other fields use preference order
                                (first non-empty value wins).
                            </Text>
                            <ImportColumnMapper
                                sourceColumns={headers}
                                targetFields={targetFields}
                                mapping={mapping}
                                onChange={setMapping}
                            />
                            {fieldsNeedingConstant.length > 0 ? (
                                <Box>
                                    <Text fontSize="sm" fontWeight="semibold" mb={3}>
                                        Default Values
                                    </Text>
                                    <VStack align="stretch" spacing={3}>
                                        {fieldsNeedingConstant.map((field) => {
                                            const custom = renderConstantField?.(
                                                field,
                                                constants[field.id] ?? '',
                                                (value) => {
                                                    setConstants((prev) => ({
                                                        ...prev,
                                                        [field.id]: value,
                                                    }));
                                                    setConstantErrors((prev) => {
                                                        if (!prev[field.id]) return prev;
                                                        const next = { ...prev };
                                                        delete next[field.id];
                                                        return next;
                                                    });
                                                },
                                                constantErrors[field.id],
                                            );
                                            if (custom) return <Box key={field.id}>{custom}</Box>;
                                            return (
                                                <FormControl
                                                    key={field.id}
                                                    isRequired
                                                    isInvalid={!!constantErrors[field.id]}
                                                >
                                                    <FormLabel fontSize="sm">{field.label}</FormLabel>
                                                    <Input
                                                        size="sm"
                                                        value={constants[field.id] ?? ''}
                                                        onChange={(e) =>
                                                            setConstants((prev) => ({
                                                                ...prev,
                                                                [field.id]: e.target.value,
                                                            }))
                                                        }
                                                    />
                                                    {constantErrors[field.id] ? (
                                                        <Text fontSize="sm" color="red.500" mt={1}>
                                                            {constantErrors[field.id]}
                                                        </Text>
                                                    ) : null}
                                                </FormControl>
                                            );
                                        })}
                                    </VStack>
                                </Box>
                            ) : null}
                            {unassignedCount > 0 ? (
                                <Text fontSize="sm" color="orange.600">
                                    Move all Unassigned columns to a field or Ignore before
                                    submitting.
                                </Text>
                            ) : null}
                        </VStack>
                    )}
                </ModalBody>
                <ModalFooter gap={2}>
                    <Button variant="outline" onClick={onClose} isDisabled={submitting}>
                        Cancel
                    </Button>
                    {viewMode === 'manual' ? (
                        <Button
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: '#3544a0' }}
                            onClick={handleOrderAssignments}
                        >
                            Order Assignments
                        </Button>
                    ) : (
                        <Button
                            bg="#0071E3"
                            color="white"
                            _hover={{ bg: '#3544a0' }}
                            onClick={handleSubmit}
                            isLoading={submitting}
                            isDisabled={!canSubmit}
                        >
                            {submitLabel}
                        </Button>
                    )}
                </ModalFooter>
            </MotionModalContent>
        </Modal>
    );
}

/** Default constant field helper for simple selects. */
export function ConstantSelect({
    value,
    onChange,
    options,
}: {
    value: string;
    onChange: (value: string) => void;
    options: Array<{ value: string; label: string }>;
}) {
    return (
        <Select size="sm" value={value} onChange={(e) => onChange(e.target.value)}>
            <option value="">Select…</option>
            {options.map((opt) => (
                <option key={opt.value} value={opt.value}>
                    {opt.label}
                </option>
            ))}
        </Select>
    );
}
